import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { Download, Landmark } from 'lucide-react';
import { downloadCsv } from '@/lib/csv';
import { formatDate, formatNumber, plural } from '@/lib/format';
import { useDebounced, useUrlParam } from '@/lib/hooks';
import { Badge, IdChip } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Notice } from '@/components/ui/feedback';
import { FilterSelect } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page';
import { TableToolbar } from '@/components/ui/toolbar';
import { orgLabel, type OrgSummary } from './derive';
import { useOrganizations } from './use-organizations';

/** The client directory, assembled from hotels, memberships and the Channex report — see derive.ts. */
export function OrganizationsPage() {
  const navigate = useNavigate();
  const { orgs, isPending, error, refetch } = useOrganizations();
  const [q, setQ] = useUrlParam('q');
  const [plan, setPlan] = useUrlParam('plan', 'all');
  const [problems, setProblems] = useUrlParam('problems', 'all');
  const query = useDebounced(q.trim().toLowerCase(), 150);
  const filtersActive = !!q || plan !== 'all' || problems !== 'all';
  const reset = () => { setQ(''); setPlan('all'); setProblems('all'); };

  const plans = useMemo(() => [...new Set(orgs.map((o) => o.plan).filter((p): p is string => !!p))].sort(), [orgs]);

  const filtered = useMemo(() => {
    let rows = orgs;
    if (query) rows = rows.filter((o) => `${orgLabel(o)} ${o.owner?.email ?? ''} ${o.hotels.map((h) => h.name).join(' ')}`.toLowerCase().includes(query) || o.id.startsWith(query));
    if (plan === 'none') rows = rows.filter((o) => !o.plan);
    else if (plan !== 'all') rows = rows.filter((o) => o.plan === plan);
    if (problems === 'errors') rows = rows.filter((o) => o.syncErrors > 0);
    if (problems === 'nochannels') rows = rows.filter((o) => o.channels === 0 && o.hotels.length > 0);
    return rows;
  }, [orgs, query, plan, problems]);

  const exportCsv = () => downloadCsv(`organizations-${new Date().toISOString().slice(0, 10)}.csv`,
    ['organizationId', 'Название', 'Владелец', 'E-mail владельца', 'Тариф', 'Отелей', 'Активных', 'Номеров', 'Сотрудников', 'Каналов', 'Ошибок синка', 'С'],
    filtered.map((o) => [o.id, o.name, o.owner?.displayName, o.owner?.email, o.plan, o.hotels.length, o.activeHotels, o.rooms, o.users.length, o.channels, o.syncErrors, o.since]));

  const columns: Column<OrgSummary>[] = [
    { key: 'name', header: 'Организация', sortValue: (o) => orgLabel(o), render: (o) => (
      <>
        <div className="flex items-center gap-2 font-semibold text-ink">
          <Link to={`/organizations/${o.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-accent-ink hover:underline">{orgLabel(o)}</Link>
          {o.syncErrors > 0 && <Badge tone="danger" dot>{plural(o.syncErrors, ['ошибка', 'ошибки', 'ошибок'])}</Badge>}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted" onClick={(e) => e.stopPropagation()}>
          <IdChip id={o.id} />
          {o.owner && <span className="truncate">{o.owner.email}</span>}
        </div>
      </>
    ) },
    { key: 'plan', header: 'Тариф', sortValue: (o) => o.plan, render: (o) => o.plan ? <Badge mono>{o.plan}</Badge> : <span className="text-xs text-faint">—</span> },
    { key: 'hotels', header: 'Отели', align: 'right', sortValue: (o) => o.hotels.length, render: (o) => <>{formatNumber(o.hotels.length)}{o.activeHotels !== o.hotels.length && <span className="text-xs text-muted"> ({o.activeHotels} акт.)</span>}</> },
    { key: 'rooms', header: 'Номера', align: 'right', sortValue: (o) => o.rooms, render: (o) => formatNumber(o.rooms) },
    { key: 'users', header: 'Сотрудники', align: 'right', sortValue: (o) => o.users.length, render: (o) => formatNumber(o.users.length) },
    { key: 'channels', header: 'Каналы', align: 'right', sortValue: (o) => o.channels, render: (o) => o.channels > 0 ? formatNumber(o.channels) : <span className="text-faint">0</span> },
    { key: 'since', header: 'С', sortValue: (o) => o.since, hideBelow: 'lg', className: 'whitespace-nowrap text-xs text-muted', render: (o) => o.since ? formatDate(o.since) : '—' },
  ];

  return (
    <>
      <PageHeader title="Организации" subtitle={`${plural(orgs.length, ['организация', 'организации', 'организаций'])} с отелями или сотрудниками`} />

      <Notice tone="neutral" className="mb-4" title="Собрано из отелей, сотрудников и отчёта Channex">
        В API пока нет списка организаций и подписок. Названия известны только у тех, кого Channex видел в этом месяце; остальные — по владельцу и id. Как появится эндпоинт, экран станет точным.
      </Notice>

      <TableToolbar
        search={{ value: q, onChange: setQ, placeholder: 'Название, владелец, отель или id' }}
        shown={filtered.length}
        total={orgs.length}
        onReset={filtersActive ? reset : null}
        actions={<Button variant="outline" size="sm" icon={<Download size={14} />} onClick={exportCsv} disabled={filtered.length === 0}>CSV</Button>}
      >
        <FilterSelect ariaLabel="Тариф" value={plan} onChange={setPlan} options={[{ value: 'all', label: 'Все тарифы' }, ...plans.map((p) => ({ value: p, label: p })), { value: 'none', label: 'Тариф неизвестен' }]} />
        <FilterSelect ariaLabel="Проблемы" value={problems} onChange={setProblems} options={[{ value: 'all', label: 'С проблемами и без' }, { value: 'errors', label: 'С ошибками синка' }, { value: 'nochannels', label: 'Без каналов' }]} />
      </TableToolbar>

      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(o) => o.id}
        defaultSort={{ key: 'hotels', dir: 'desc' }}
        loading={isPending}
        error={error}
        onRetry={refetch}
        empty={{ icon: <Landmark size={28} />, title: orgs.length ? 'Ничего не подошло под фильтры' : 'Организаций пока нет', action: filtersActive ? <Button size="sm" variant="ghost" onClick={reset}>Сбросить фильтры</Button> : undefined }}
        onRowClick={(o) => navigate(`/organizations/${o.id}`)}
      />
    </>
  );
}
